import { Geofence } from '../../../API/types/Geofences';
import {
  Locations, LocationsArray, Location, LocationEntity,
} from './types';
import { MIN_GEOFENCE_AREA_SQUARE_METERS, MIN_GEOFENCE_RADIUS } from './constants';

const OVERLAY_Z_IDX = 100;

function copy(out: Location, a: Location): Location {
  // eslint-disable-next-line
  out[0] = a[0];
  // eslint-disable-next-line
  out[1] = a[1];

  return out;
}

function smooth(points: Locations): Locations {
  const output = [];

  if (points.length > 0) {
    output.push(copy([0, 0], points[0]));
  }

  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i];
    const p1 = points[i + 1];
    const p0x = p0[0];
    const p0y = p0[1];
    const p1x = p1[0];
    const p1y = p1[1];

    output.push([0.85 * p0x + 0.15 * p1x, 0.85 * p0y + 0.15 * p1y]);
    output.push([0.15 * p0x + 0.85 * p1x, 0.15 * p0y + 0.85 * p1y]);
  }

  if (points.length > 1) {
    output.push(copy([0, 0], points[points.length - 1]));
  }

  return output;
}

function convertToGooglePath(path: any): google.maps.LatLngLiteral[] {
  return path.map((item: Location) => ({ lat: item[0], lng: item[1] }));
}

function smoothPath(path: Locations): Locations {
  const smoothedPath = smooth(smooth(path));
  return smoothedPath;
}

function getSmoothGooglePath(path: Locations): google.maps.LatLngLiteral[] {
  return convertToGooglePath(smoothPath(path));
}

function getAngle(path: Locations): number {
  if (path.length < 2) {
    return 0;
  }

  const [first, second] = path;
  const angle = Math.atan2(first[1] - second[1], first[0] - second[0]) * (180 / Math.PI);
  return (angle + 360) % 360;
}

function getPosition(path: Locations): google.maps.LatLngLiteral {
  if (path?.length === 0) {
    console.warn('path should have more than 1 element');
    return { lat: 0, lng: 0 };
  }
  return { lat: path[0][0], lng: path[0][1] };
}

function getEndPosition(path: Locations): google.maps.LatLngLiteral {
  if (path?.length === 0) {
    console.warn('path should have more than 1 element');
    return { lat: 0, lng: 0 };
  }
  const location = path[path.length - 1];
  return { lat: location[0], lng: location[1] };
}

function devideArrayTail(path: any): any {
  const tailIdx = Math.floor(path.length * 0.7);
  return {
    main: path.slice(0, tailIdx),
    tail: path.slice(tailIdx - 1),
  };
}

function getDefaultCenterAndZoom(locations: LocationsArray): {
  center: google.maps.LatLngLiteral, zoom: number } {
  if (locations.length < 1 || !locations?.[0]?.[0]?.[0]) {
    // console.warn('There is no data to get center');
    return { center: { lat: 0, lng: 0 }, zoom: 1 };
  }

  let maxLat = locations[0][0][0];
  let minLat = locations[0][0][0];
  let maxLng = locations[0][0][1];
  let minLng = locations[0][0][1];
  locations.forEach((location) => {
    if (location[0][0] !== 0) {
      maxLat = Math.max(maxLat, location[0][0], location[location.length - 1][0]);
      minLat = Math.min(minLat, location[0][0], location[location.length - 1][0]);
    }
    if (location[0][1] !== 0) {
      maxLng = Math.max(maxLng, location[0][1], location[location.length - 1][1]);
      minLng = Math.min(minLng, location[0][1], location[location.length - 1][1]);
    }
  });

  const centerLat = (minLat + maxLat) / 2;
  const centerLng = (minLng + maxLng) / 2;

  const GLOBE_WIDTH = 256;
  const lngAngle = (maxLng === minLng) ? 2 : maxLng - minLng;
  const pixelWidth = 100;
  const zoom = Math.round(Math.log((pixelWidth * 360) / lngAngle / GLOBE_WIDTH) / Math.LN2);

  return { center: { lat: centerLat, lng: centerLng }, zoom };
}

function getPositionKey(lat: number, lng: number): string {
  return `${lat},${lng}`;
}

// Function to calculate the distance between two coordinates using the Haversine formula
function calculateDistance(coord1: number[], coord2: number[]): number {
  const toRad = (value: number): number => (value * Math.PI) / 180;

  const lat1 = coord1[0];
  const lon1 = coord1[1];
  const lat2 = coord2[0];
  const lon2 = coord2[1];

  const earthRadius = 6371000; // Earth's radius in meters

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const distance = earthRadius * c; // Distance in meters

  return distance;
}

function findNearestCoord(coord: Location, coords: Locations): Location {
  let minDistance = Infinity;
  let nearestCoord = coord;
  coords.forEach((c) => {
    const distance = calculateDistance(c, coord);
    if (distance < minDistance) {
      minDistance = distance;
      nearestCoord = c;
    }
  });

  return nearestCoord;
}

const siftCoordinates = (coordinates: LocationEntity[], maxDistanceBetweenPoints = 300): LocationEntity[] => {
  const rangedPoints = [coordinates[0]];
  let currentDistance = 0;
  for (let i = 1; i < coordinates.length; i += 1) {
    const prevCoord = coordinates[i - 1];
    const currentCoord = coordinates[i];

    // Calculate the distance between the current point and the last point
    const distance = calculateDistance(currentCoord.location, prevCoord.location);

    if (currentDistance + distance >= maxDistanceBetweenPoints) {
      rangedPoints.push(prevCoord);
      currentDistance = 0;
    } else {
      currentDistance += distance;
    }
  }

  rangedPoints.push(coordinates[coordinates.length - 1]);
  return rangedPoints;
};

interface LatLng {
  lat: number;
  lng: number;
}

function convertCircleToPolygon(center: LatLng, radius: number, numSides = 32): number[][] {
  const points: number[][] = [];
  const degreeStep = 360 / numSides;

  for (let i = 0; i < numSides; i += 1) {
    const gpos = google.maps.geometry.spherical.computeOffset(center, radius, degreeStep * i);
    points.push([gpos.lng(), gpos.lat()]);
  }

  points.push(points[0]);

  return points;
}

function isCirclePolygon(g: Geofence): Boolean {
  const geofence = g.location?.coordinates?.[0];
  return Boolean(geofence && geofence.length === 33);
}

function getCircleParams(g: Geofence): { center: LatLng, radius: number } {
  const geofence = g?.location?.coordinates?.[0];
  if (!geofence) return { center: { lat: 0, lng: 0 }, radius: 0 };

  const center = {
    lat: (geofence[0][1] + geofence[16][1]) / 2,
    lng: (geofence[0][0] + geofence[16][0]) / 2,
  };
  const point = new google.maps.LatLng(geofence[0][1], geofence[0][0]);
  const radius = Math.round(
    google?.maps?.geometry?.spherical
      ?.computeDistanceBetween(new google.maps.LatLng(center.lat, center.lng), point) || 0,
  );

  return { center, radius };
}

const hasExactlyFiveDecimals = (value: string): boolean => /^-?\d+\.\d{5}$/.test(value.trim());

const formatCoordinateToFiveDecimals = (value: number): string => value.toFixed(5);

const COORD_EPSILON = 1e-9;

const coordsEqual = (a: number[], b: number[]): boolean => (
  Math.abs(a[0] - b[0]) < COORD_EPSILON && Math.abs(a[1] - b[1]) < COORD_EPSILON
);

const removeConsecutiveDuplicates = (coordinates: number[][]): number[][] => {
  if (coordinates.length === 0) {
    return [];
  }

  const result = [coordinates[0]];
  for (let i = 1; i < coordinates.length; i += 1) {
    if (!coordsEqual(coordinates[i], result[result.length - 1])) {
      result.push(coordinates[i]);
    }
  }

  return result;
};

const getUniquePolygonVertices = (coordinates: number[][]): number[][] => {
  const deduped = removeConsecutiveDuplicates(coordinates);
  if (
    deduped.length > 1
    && coordsEqual(deduped[0], deduped[deduped.length - 1])
  ) {
    return deduped.slice(0, -1);
  }

  return deduped;
};

const arePointsCollinear = (p1: number[], p2: number[], p3: number[]): boolean => {
  const cross = (p2[0] - p1[0]) * (p3[1] - p1[1]) - (p2[1] - p1[1]) * (p3[0] - p1[0]);
  return Math.abs(cross) < COORD_EPSILON;
};

const areAllPointsCollinear = (vertices: number[][]): boolean => {
  if (vertices.length < 3) {
    return true;
  }

  const [first, second] = vertices;
  return vertices.slice(2).every((point) => arePointsCollinear(first, second, point));
};

const approximatePolygonAreaSquareMeters = (vertices: number[][]): number => {
  let area = 0;
  const vertexCount = vertices.length;

  for (let i = 0; i < vertexCount; i += 1) {
    const j = (i + 1) % vertexCount;
    area += vertices[i][0] * vertices[j][1];
    area -= vertices[j][0] * vertices[i][1];
  }

  const areaSquareDegrees = Math.abs(area) / 2;
  const averageLatitude = vertices.reduce((sum, [, lat]) => sum + lat, 0) / vertexCount;
  const metersPerDegreeLat = 111320;
  const metersPerDegreeLng = 111320 * Math.cos((averageLatitude * Math.PI) / 180);

  return areaSquareDegrees * metersPerDegreeLat * metersPerDegreeLng;
};

const computePolygonAreaSquareMeters = (coordinates: number[][]): number => {
  const vertices = getUniquePolygonVertices(coordinates);
  if (vertices.length < 3) {
    return 0;
  }

  if (typeof google !== 'undefined' && google?.maps?.geometry?.spherical) {
    const path = vertices.map(([lng, lat]) => new google.maps.LatLng(lat, lng));
    path.push(path[0]);
    return google.maps.geometry.spherical.computeArea(path);
  }

  return approximatePolygonAreaSquareMeters(vertices);
};

const getPolygonValidationError = (coordinates: number[][]): string | null => {
  if (coordinates.length < 4) {
    return null;
  }

  for (let i = 1; i < coordinates.length; i += 1) {
    if (coordsEqual(coordinates[i], coordinates[i - 1])) {
      return 'Polygon must not contain duplicate consecutive vertices';
    }
  }

  const uniqueVertices = getUniquePolygonVertices(coordinates);
  if (uniqueVertices.length < 3) {
    return 'A polygon must have at least 3 distinct vertices';
  }

  if (areAllPointsCollinear(uniqueVertices)) {
    return 'Polygon vertices must not all lie on the same line';
  }

  const area = computePolygonAreaSquareMeters(coordinates);
  if (area < MIN_GEOFENCE_AREA_SQUARE_METERS) {
    return `Polygon area must be large enough to form a valid geofence (minimum ~${MIN_GEOFENCE_RADIUS}m radius)`;
  }

  return null;
};

export {
  OVERLAY_Z_IDX,
  smooth,
  convertToGooglePath,
  smoothPath,
  getSmoothGooglePath,
  getAngle,
  getPosition,
  getEndPosition,
  devideArrayTail,
  getDefaultCenterAndZoom,
  getPositionKey,
  calculateDistance,
  findNearestCoord,
  siftCoordinates,
  convertCircleToPolygon,
  isCirclePolygon,
  getCircleParams,
  hasExactlyFiveDecimals,
  formatCoordinateToFiveDecimals,
  getPolygonValidationError,
};
